import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import Icon from '@ui/Icon';
import ActionMenu from '../../ActionMenu';
import { CommentData } from '../types';
import CommentsSidebarEditor from './CommentsSidebarEditor';
import CommentFeed from './CommentFeed';
import CommentsFailedError from './CommentsFailedError';
import Tooltip from '../../Tooltip';
import usePrevious from '../../../lib/usePrevious';
import { useLayoutContext } from '../../BaseLayout/context/LayoutContext';
import useEllipsisDisplay from '../../../util/hooks/useEllipsisDisplay';

interface CommentsSidebarProps {
  clipId: string;
  clipName: string;
  comments: CommentData[];
  loading?: boolean;
  error?: string;
  renderComment: (props: { item: CommentData; index: number }) => JSX.Element;
  onMarkAllAsViewed: () => void;
  onSend: (text: string) => Promise<void>;
  onClose: () => void;
  onRefresh: () => void;
}

enum ClipCommentActions {
  MarkAll = 'Mark all as read',
}

const CommentsSidebarHeader = ({
  onClose,
  closeButtonRef,
}: {
  onClose: () => void;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
}) => {
  const { classes } = useStyles();

  return (
    <div className={classes.sectionContainer}>
      <Typography variant="h5" bold component="h2">
        Comments
      </Typography>
      <div className={classes.actionButtonContainer}>
        <Button
          variant="icon"
          ref={closeButtonRef}
          aria-label={'Close comments'}
          className={classes.iconButton}
          onClick={onClose}
        >
          <Icon type="ex" />
        </Button>
      </div>
    </div>
  );
};

const CommentRefreshButton = ({
  loading,
  onRefresh,
}: {
  loading?: boolean;
  onRefresh: () => void;
}) => {
  const { classes } = useStyles();
  return (
    <Tooltip text="Refresh">
      <Button
        variant="icon"
        aria-label={'refresh comments'}
        onClick={onRefresh}
        disabled={loading}
        className={classes.iconButton}
      >
        <Icon type="replay" />
      </Button>
    </Tooltip>
  );
};

const CommentsSidebarClipHeader = ({
  clipId,
  clipName,
  commentsLength,
  onMarkAllAsViewed,
  loading,
  onRefresh,
}: {
  clipId: string;
  clipName: string;
  commentsLength: number;
  loading?: boolean;
  onRefresh: () => void;
  onMarkAllAsViewed: () => void;
}) => {
  const { classes } = useStyles();
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const { tooltipDisplay, target } = useEllipsisDisplay({
    title: clipName,
  });

  const handleTooltipOpen = () => {
    setTooltipOpen(true);
  };
  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  return (
    <div
      className={classes.sectionContainer}
      style={{ borderTop: `1px solid ${palette.gray_800}` }}
    >
      <Tooltip
        open={tooltipDisplay && tooltipOpen}
        text={clipName}
        placement="top-start"
        onOpen={handleTooltipOpen}
        onClose={handleTooltipClose}
      >
        <div ref={target} className={classes.ellipsisContainer}>
          <Typography
            variant="h5"
            bold
            component="h3"
            className={classes.ellipsisContainer}
          >
            {clipName}
          </Typography>
        </div>
      </Tooltip>
      <div className={classes.actionButtonContainer}>
        <CommentRefreshButton loading={loading} onRefresh={onRefresh} />
        <ActionMenu
          id={`wsl-comments-clip-${clipId}-actions`}
          classes={{
            iconButton: classes.iconButton,
          }}
          aria-label="Comment thread actions"
          actions={ClipCommentActions}
          onClick={onMarkAllAsViewed}
          PermissionsRecord={{
            [ClipCommentActions.MarkAll]: {
              show: true,
              // Disable the menu item when empty, but keep the trigger focusable (tab order)
              disabled: commentsLength === 0,
            },
          }}
          IconRecord={{}}
          MenuRecord={{}}
        />
      </div>
    </div>
  );
};

const CommentsSidebar = (props: CommentsSidebarProps) => {
  const {
    clipId,
    clipName,
    comments,
    loading,
    error: initialError,
    renderComment,
    onMarkAllAsViewed,
    onSend,
    onClose,
    onRefresh,
  } = props;
  const { classes } = useStyles();
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState<string>('');
  const prevClipId = usePrevious(clipId);
  const { toggleRightRailScrollable } = useLayoutContext();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Move keyboard focus into the panel when it opens so tab nav continues through the rail
   */
  useLayoutEffect(() => {
    closeButtonRef.current?.focus();
  }, [clipId]);

  /**
   * If we switch clip comments, reset
   */
  useEffect(() => {
    if (prevClipId !== clipId) {
      setError('');
    }
  }, [clipId, prevClipId]);

  useEffect(() => {
    toggleRightRailScrollable(false);

    return () => {
      toggleRightRailScrollable(true);
    };
  }, [toggleRightRailScrollable]);

  const scrollToBottom = React.useCallback(() => {
    const scrollContainer = document.getElementById('wsl-comment-scroll');
    if (scrollContainer)
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth',
      });
  }, []);

  const handleSend = React.useCallback(
    (value: string) => {
      return onSend(value)
        .then(
          () =>
            new Promise<void>((resolve, reject) => {
              setText('');
              setError('');
              scrollToBottom();
              resolve();
            })
        )
        .catch((err: Error) => {
          setError(err.message);
        });
    },
    [onSend, scrollToBottom]
  );

  return (
    <div
      className={classes.container}
      role="region"
      aria-label="Comments panel"
    >
      <CommentsSidebarHeader
        onClose={onClose}
        closeButtonRef={closeButtonRef}
      />
      <CommentsSidebarClipHeader
        clipId={clipId}
        clipName={clipName}
        commentsLength={comments.length}
        loading={loading}
        onMarkAllAsViewed={onMarkAllAsViewed}
        onRefresh={onRefresh}
      />
      {initialError && <CommentsFailedError onRefresh={onRefresh} />}
      {!initialError && (
        <>
          <CommentFeed
            comments={comments}
            renderComment={renderComment}
            loading={loading}
            clipId={clipId}
          />
          <div className={classes.footer}>
            <CommentsSidebarEditor
              text={text}
              error={error}
              onChange={setText}
              onSubmit={handleSend}
            />
          </div>
        </>
      )}
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  ellipsisContainer: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  sectionContainer: {
    display: 'flex',
    padding: theme.spacing(2, 1.5),
    alignItems: 'center',
  },
  actionButtonContainer: {
    marginLeft: 'auto',
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  iconButton: {
    color: palette.gray_0,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  footer: {
    marginTop: 'auto',
    marginBottom: theme.spacing(13.5),
    borderTop: `1px solid ${palette.gray_800}`,
  },
}));

export default CommentsSidebar;
